import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronRight, Package, Info } from 'lucide-react';
import { getLingotto, isValidLingotto, LINGOTTI_SLUGS, LINGOTTI_ORO } from '@/lib/lingottiOroData';
import { useStore } from '@/store/useStore';
import { FALLBACK_METAL_PRICES } from '@/server/config/ssr-config';

interface Props { ssrMetalPrices?: any; }

export default function LingottoOroSingolo({ ssrMetalPrices }: Props) {
  const { taglio } = useParams<{ taglio: string }>();
  const { currentGoldPrice } = useStore();

  if (!taglio || !isValidLingotto(taglio)) {
    return <Navigate to="/lingotti-oro-investimento" replace />;
  }

  const l = getLingotto(taglio)!;
  const goldPrice24K =
    currentGoldPrice?.price ?? ssrMetalPrices?.gold?.price ?? FALLBACK_METAL_PRICES.gold.price;

  const valoreOroFino = goldPrice24K * l.pesoGrammi;
  // Premium decrescente al crescere del peso
  const premiumPct =
    l.pesoGrammi >= 1000 ? 0.015 :
    l.pesoGrammi >= 100  ? 0.03  :
    l.pesoGrammi >= 50   ? 0.04  :
    l.pesoGrammi >= 20   ? 0.05  :
    l.pesoGrammi >= 10   ? 0.065 :
    l.pesoGrammi >= 5    ? 0.095 :
    0.22;
  const valoreMercato = valoreOroFino * (1 + premiumPct);

  const fmtEuro = (v: number) =>
    `${Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`;
  const fmtPrice = (v: number) => v.toFixed(2).replace('.', ',');

  const canonical = `https://www.gildy.it/lingotti-oro/${l.slug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.gildy.it/' },
          { '@type': 'ListItem', position: 2, name: 'Lingotti oro investimento', item: 'https://www.gildy.it/lingotti-oro-investimento' },
          { '@type': 'ListItem', position: 3, name: l.nome, item: canonical },
        ],
      },
      {
        '@type': 'Product',
        name: l.nome,
        description: l.intro,
        category: 'Lingotto da investimento in oro 999,9‰',
        weight: { '@type': 'QuantitativeValue', value: l.pesoGrammi, unitCode: 'GRM' },
        material: 'Oro 999,9‰ (24kt)',
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'EUR',
          lowPrice: Math.round(valoreOroFino),
          highPrice: Math.round(valoreMercato * 1.05),
          availability: 'https://schema.org/InStock',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: l.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{l.titleTag}</title>
        <meta name="description" content={l.metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:title" content={l.titleTag} />
        <meta property="og:description" content={l.metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="product" />
        <meta property="og:locale" content="it_IT" />
        <meta property="og:site_name" content="Gildy" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <nav className="flex items-center text-sm text-muted-foreground mb-4" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-4 w-4 mx-1" />
          <Link to="/lingotti-oro-investimento" className="hover:text-foreground">Lingotti oro</Link>
          <ChevronRight className="h-4 w-4 mx-1" />
          <span className="text-foreground">{l.nome}</span>
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{l.h1}</h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8">{l.intro}</p>

        <Card className="mb-8 border-gold/30 bg-gradient-to-br from-gold/5 to-transparent">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Package className="h-5 w-5 text-gold" />
              Quotazione indicativa oggi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><span className="text-muted-foreground">Peso:</span> <strong>{l.pesoGrammi}g</strong></div>
              <div><span className="text-muted-foreground">Purezza:</span> <strong>999,9‰ (24kt)</strong></div>
              <div><span className="text-muted-foreground">Formato:</span> <strong>{l.formato === 'minted' ? 'Coniato' : 'Fuso'}</strong></div>
            </div>
            <div className="border-t border-gold/20 pt-3 space-y-1">
              <p className="text-sm">
                Spot oro 24kt: <strong>{fmtPrice(goldPrice24K)} €/g</strong> ·
                Valore oro fino: <strong>{fmtEuro(valoreOroFino)}</strong>
              </p>
              <p className="text-base">
                Valore di mercato (premium ~{(premiumPct * 100).toFixed(1)}%):{' '}
                <strong className="text-gold text-lg">{fmtEuro(valoreMercato)}</strong>
              </p>
              <p className="text-xs text-muted-foreground">
                Quotazione basata sul prezzo spot LBMA + premium medio di mercato per lingotti certificati in blister originale.
              </p>
            </div>
          </CardContent>
        </Card>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Info className="h-5 w-5 text-metal-primary" />
            Premium di mercato
          </h2>
          <p className="text-muted-foreground leading-relaxed">{l.premiumInfo}</p>
        </section>

        {l.faqs.length > 0 && (
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">Domande frequenti</h2>
            <div className="space-y-4">
              {l.faqs.map((f, idx) => (
                <Card key={idx}>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-foreground mb-2">{f.q}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{f.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12 pt-8 border-t">
          <h2 className="text-xl font-bold text-foreground mb-4">Altri tagli di lingotti</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
            {LINGOTTI_SLUGS.filter((s) => s !== l.slug).map((s) => (
              <Link key={s} to={`/lingotti-oro/${s}`} className="text-sm text-metal-primary hover:underline">
                {LINGOTTI_ORO[s].nome}
              </Link>
            ))}
          </div>
          <Card className="bg-gradient-to-r from-gold/10 to-transparent border-gold/30">
            <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5">
              <p className="text-sm">
                <strong>Vuoi vendere un lingotto?</strong> Confronta i compro oro vicino a te con spread trasparente.
              </p>
              <Link to="/compro-oro-vicino-a-me" className="text-sm font-medium text-gold hover:text-gold-light whitespace-nowrap">
                Trova compro oro vicino a te →
              </Link>
            </CardContent>
          </Card>
        </section>
      </div>
    </>
  );
}
